import { SaveOutlined } from "@ant-design/icons";
import { Button, Col, Flex, Form, Input, Modal, Row } from "antd";
import React, {
  forwardRef,
  useCallback,
  useEffect,
  useImperativeHandle,
  useState,
} from "react";
import { createUseStyles } from "react-jss";
import useCustomerManagementApi from "../../apiHooks/useCustomerManagementApi";
import { _STYLES } from "../../common/utils/customStyles";
import { useNotificationContext } from "../../core/contexts/NotificationContextProvider";

const { mt1, textSm3, uploadLogo } = _STYLES;
const useStyles = createUseStyles({
  mt1,
  textSm3,
  uploadLogo,
});

const CustomerManagementModal = forwardRef(
  ({ isModelOpen, toggleModal, cbOnSave, isDisabled }, ref) => {
    const classes = useStyles();

    const [selectedRecord, setSelectedRecord] = useState(null);

    const { notify } = useNotificationContext();

    const {
      updateCustomerApi: { execute: updateCustomer, isLoading: isUpdating },
    } = useCustomerManagementApi();

    useImperativeHandle(ref, () => ({
      async getCustomerRecord(data) {
        setSelectedRecord(data);
      },
    }));

    const [form] = Form.useForm();

    const handleSubmit = useCallback(
      async (data) => {
        data = {
          ...data,
          CustomerID: selectedRecord.customerID,
        };
        const res = await updateCustomer(data);
        if (res) {
          toggleModal();
          notify({
            message: `Customer updated successfully`,
          });
          cbOnSave?.();
        }
      },
      [cbOnSave, notify, selectedRecord, toggleModal, updateCustomer]
    );

    useEffect(() => {
      if (!isModelOpen) {
        form.resetFields();
        setSelectedRecord(null);
      }
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isModelOpen]);

    useEffect(() => {
      if (selectedRecord) {
        form.setFieldsValue({
          Email: selectedRecord.email,
          MobileNo: selectedRecord.mobileNo,
          FirstName: selectedRecord.firstName,
          LastName: selectedRecord.lastName,
          NIC: selectedRecord.nic,
        });
      }
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [selectedRecord]);

    return (
      <>
        <Modal
          centered
          title={isDisabled ? "View Customer" : "Configure Customer"}
          open={isModelOpen}
          onCancel={() => toggleModal()}
          footer={null}
        >
          <Row className={classes.mt1}>
            <Col xs={24}>
              <Form
                name="customer"
                initialValues={{ remember: false }}
                onFinish={handleSubmit}
                autoComplete="off"
                layout="vertical"
                form={form}
              >
                <Form.Item
                  label="First Name"
                  name="FirstName"
                  rules={[
                    {
                      required: true,
                      message: "Please enter First Name",
                    },
                  ]}
                >
                  <Input
                    disabled={isDisabled}
                    placeholder="First Name"
                    allowClear
                  />
                </Form.Item>
                <Form.Item
                  label="Last Name"
                  name="LastName"
                  rules={[
                    {
                      required: true,
                      message: "Please enter Last Name",
                    },
                  ]}
                >
                  <Input
                    disabled={isDisabled}
                    placeholder="Last Name"
                    allowClear
                  />
                </Form.Item>
                <Form.Item
                  label="Email"
                  name="Email"
                  rules={[
                    {
                      required: true,
                      message: "Please enter an E-mail address",
                    },
                    {
                      type: "email",
                      message: "The input is not valid E-mail Address!",
                    },
                  ]}
                >
                  <Input disabled={isDisabled} allowClear />
                </Form.Item>
                <Form.Item
                  label="NIC"
                  name="NIC"
                  rules={[
                    {
                      required: true,
                      message: "Please enter an NIC number",
                    },
                    {
                      pattern: /^([0-9]{9}[vVxX]|[0-9]{12})$/,
                      message: "Please enter a valid NIC number.",
                    },
                  ]}
                >
                  <Input disabled={isDisabled} placeholder="NIC" allowClear />
                </Form.Item>
                <Form.Item
                  name="MobileNo"
                  label="Phone Number"
                  rules={[
                    {
                      required: true,
                      message: "Please input Phone Number!",
                    },
                    {
                      pattern: /^[^0]/,
                      message: "Phone number must not start with 0",
                    },
                    {
                      pattern: new RegExp(/^7[0-9]{8}$/),
                      message: "Please input a valid Phone Number!",
                    },
                  ]}
                >
                  <Input
                    disabled={isDisabled}
                    placeholder="7XXXXXXXX"
                    addonBefore={"+94"}
                    allowClear
                    maxLength={9}
                  />
                </Form.Item>
                {!isDisabled && (
                  <Flex justify="flex-end">
                    <Button
                      icon={<SaveOutlined />}
                      loading={isUpdating}
                      type="primary"
                      htmlType="submit"
                    >
                      Update
                    </Button>
                  </Flex>
                )}
              </Form>
            </Col>
          </Row>
        </Modal>
      </>
    );
  }
);

export default CustomerManagementModal;
